import { ArrowForward, DeleteOutline } from '@mui/icons-material'
import {
  Alert,
  Button,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { orderType } from '../types'
import dayjs from 'dayjs'
import './Drafts.css'
import { useNavigate } from 'react-router'
import { useUser } from '../hooks'

const COLUMN_WIDTHS = {
  QuoteNumber: undefined,
  CustomerPO: undefined,
  LastUpdated: undefined,
  Continue: '125px',
  Delete: '80px',
}

export default function Drafts(props: { open: boolean; onClose: () => void }) {
  const user = useUser()
  const navigate = useNavigate()

  const [drafts, setDrafts] = useState<orderType[]>([])
  const [loadingDrafts, setLoadingDrafts] = useState(false)
  const [deleteDraftStatus, setDeleteDraftStatus] = useState<'success' | 'error' | null>(null)

  useEffect(() => {
    if (props.open) {
      setLoadingDrafts(true)
      axios
        .get(`/getDrafts/${user.email}`)
        .then(res => {
          setDrafts(res.data)
        })
        .catch(err => {
          console.error(err)
          setDrafts([])
          // TODO: error ui
        })
        .finally(() => {
          setLoadingDrafts(false)
        })
    }
  }, [props.open])

  const handleClose = () => {
    props.onClose()
  }

  const handleDelete = (draft: orderType) => {
    setDeleteDraftStatus(null)

    axios
      .delete(`/deleteDraft/${draft.Id}`)
      .then(() => {
        setDrafts(drafts.filter(d => d.Id !== draft.Id))
        setDeleteDraftStatus('success')
      })
      .catch(err => {
        console.error(err)
        setDeleteDraftStatus('error')
      })
  }

  return (
    <>
      <Drawer open={props.open} onClose={handleClose} anchor="right">
        <div className="drafts">
          <div className="drafts-header">
            <Typography variant="h6">Drafts</Typography>
            <IconButton onClick={handleClose}>
              <ArrowForward />
            </IconButton>
          </div>
          <Divider flexItem />
          <Table color="primary">
            <TableHead>
              <TableCell width={COLUMN_WIDTHS.QuoteNumber}>Quote #</TableCell>
              <TableCell width={COLUMN_WIDTHS.CustomerPO}>Customer PO</TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.LastUpdated}>
                Last Updated
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.Continue} />
              <TableCell width={COLUMN_WIDTHS.Delete} />
            </TableHead>
            <TableBody>
              {loadingDrafts
                ? Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <TableCell key={index}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : drafts.map(draft => (
                    <TableRow key={draft.Id}>
                      <TableCell>
                        {draft.QuoteNumber === null || draft.QuoteNumber === '' ? '-' : draft.QuoteNumber}
                      </TableCell>
                      <TableCell>
                        {draft.CustomerPO === null || draft.CustomerPO === '' ? '-' : draft.CustomerPO}
                      </TableCell>
                      <TableCell align="right">
                        {dayjs(draft.SubmissionTimestamp).format('MM/DD/YYYY hh:mm:ss A')}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            navigate(`/order/draft/${draft.Id}`)
                            handleClose()
                          }}
                        >
                          Continue
                        </Button>
                      </TableCell>
                      <TableCell>
                        <IconButton>
                          <DeleteOutline onClick={() => handleDelete(draft)} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
      </Drawer>
      <Snackbar
        open={deleteDraftStatus === 'success'}
        autoHideDuration={5000}
        onClose={() => setDeleteDraftStatus(null)}
        message="Draft deleted successfully"
      />
      <Snackbar open={deleteDraftStatus === 'error'}>
        <Alert severity="error" variant="filled" onClose={() => setDeleteDraftStatus(null)}>
          Failed to delete draft. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}
